/**
 * MemberDataProcessor - Handles member data processing and formatting
 * 
 * This service manages member data transformation, tag processing, and
 * member display formatting for the Roster feature.
 * 
 * Key responsibilities:
 * - Process raw member data into display format
 * - Handle tag ID to name conversion
 * - Calculate member statistics and summaries
 * - Format member data for different display contexts
 * - Handle member data validation and sanitization
 */

import { 
  safeString, 
  EMPTY_MONTHLY_ALLOCATIONS,
  getLocationDisplayName,
  determineAllocationStatus,
  calculateMaxAllocation,
  hasOverAllocation
} from '../utils/rosterCalculations';

class MemberDataProcessor {
  constructor() {
    this.processedMemberCache = new Map();
    this.tagConversionCache = new Map();
    this.lastTagsHash = '';
  }

  /**
   * Process roster members with tag conversion and validation
   */
  processRosterMembers(membersData, availableTags = []) {
    if (!Array.isArray(membersData)) {
      console.warn('MemberDataProcessor: Invalid members data format, expected array');
      return [];
    }

    // Clear tag cache if tags changed
    const currentTagsHash = JSON.stringify(availableTags.map(t => ({ id: t.id, name: t.name })));
    if (currentTagsHash !== this.lastTagsHash) {
      this.tagConversionCache.clear();
      this.lastTagsHash = currentTagsHash;
      console.log('MemberDataProcessor: Cleared tag conversion cache due to tags change');
    }

    return membersData
      .filter(member => member && member.id && member.name)
      .map(member => this.processSingleMember(member, availableTags));
  }

  /**
   * Process a single member with all transformations
   */
  processSingleMember(member, availableTags = []) {
    const processedMember = {
      ...member,
      // Sanitize core fields
      name: safeString(member.name),
      company: safeString(member.company, "CA"),
      location: safeString(member.location, "US"),
      function: safeString(member.function, "Other"),
      focus: safeString(member.focus),
      department: safeString(member.department),
      role: safeString(member.role),
      
      // Process tags
      tags: member.tags || [],
      tagNames: this.convertTagIdsToNames(member.tags || [], availableTags),
      
      // Initialize allocation fields
      staffedAllocations: [...EMPTY_MONTHLY_ALLOCATIONS],
      outcomeNames: [],
      memberApplications: new Set(),
      allocationStatus: 'Not Allocated',
      maxAllocation: 0,
      overAllocation: {
        hasOverAllocation: false,
        maxAllocation: 0
      },
      
      // Add computed display fields
      displayLocation: getLocationDisplayName(member.location),
      displayFunction: this.formatMemberFunction(member),
      
      // Validation flags
      isValid: this.validateMember(member),
      validationErrors: this.getMemberValidationErrors(member)
    };

    return processedMember;
  }

  /**
   * Convert tag IDs to tag names with caching
   */
  convertTagIdsToNames(tagIds, availableTags) {
    if (!Array.isArray(tagIds) || tagIds.length === 0) {
      return new Set();
    }

    const cacheKey = JSON.stringify(tagIds.sort());
    if (this.tagConversionCache.has(cacheKey)) {
      return this.tagConversionCache.get(cacheKey);
    }

    const memberTagNames = new Set();
    
    tagIds.forEach(tagId => {
      const tag = availableTags.find(t => t.id === tagId || t.id === String(tagId));
      if (tag && tag.name) {
        memberTagNames.add(tag.name);
      } else {
        // Fallback: if tag is already a string (name), use it directly
        if (typeof tagId === 'string' && !tagId.match(/^\d+$/) && !tagId.match(/^[0-9a-f-]{36}$/)) {
          memberTagNames.add(tagId);
        } else {
          // For UUID tags that don't exist, show a generic label
          if (typeof tagId === 'string' && tagId.match(/^[0-9a-f-]{36}$/)) {
            memberTagNames.add('Unknown Tag');
          } else {
            // For numeric IDs, show with prefix
            memberTagNames.add(`Tag-${tagId}`);
          }
        }
      }
    });

    // Cache the result
    this.tagConversionCache.set(cacheKey, memberTagNames);
    return memberTagNames;
  }

  /**
   * Format member function display with focus
   */
  formatMemberFunction(member) {
    if (member.function === 'Developer' && member.focus) {
      return `${member.focus} Developer`;
    }
    return member.function || 'Other';
  }

  /**
   * Process member with allocation data
   */
  processMemberWithAllocations(member, memberOutcomes, availableTags = []) {
    const processedMember = this.processSingleMember(member, availableTags);
    
    if (Object.keys(memberOutcomes).length > 0) {
      // Calculate staffed allocations
      const staffedAllocations = [...EMPTY_MONTHLY_ALLOCATIONS];
      const outcomeNames = [];
      const memberApplications = new Set();
      
      Object.values(memberOutcomes).forEach(outcome => {
        outcomeNames.push(outcome.outcome_name);
        
        outcome.staffed.forEach((val, idx) => {
          staffedAllocations[idx] += val;
        });
        
        outcome.applications.forEach(app => {
          memberApplications.add(app);
        });
      });
      
      const maxAllocation = calculateMaxAllocation(staffedAllocations);
      const allocationStatus = determineAllocationStatus(maxAllocation);
      const overAllocation = hasOverAllocation(staffedAllocations);
      
      // Update processed member with allocation data
      Object.assign(processedMember, {
        staffedAllocations,
        outcomeNames,
        memberApplications,
        allocationStatus,
        maxAllocation,
        overAllocation
      });
    }
    
    return processedMember;
  }

  /**
   * Calculate member data summary
   */
  calculateMemberSummary(roster, outcomeAllocations, year) {
    const summary = {
      totalMembers: roster.length,
      totalAssigned: 0,
      hasAllocationData: outcomeAllocations.length > 0,
      byCompany: {},
      byLocation: {},
      byFunction: {},
      byAllocationStatus: {
        'Not Allocated': 0,
        'Under Allocated': 0,
        'Fully Allocated': 0,
        'Over Allocated': 0
      },
      tagDistribution: {},
      validationIssues: 0
    };

    if (roster.length === 0) {
      return summary;
    }

    // Get assigned member IDs
    const assignedMemberIds = new Set();
    if (outcomeAllocations.length > 0) {
      outcomeAllocations.forEach(outcome => {
        if (outcome.staffed_roles) {
          outcome.staffed_roles.forEach(role => {
            if (role.member_id) {
              assignedMemberIds.add(role.member_id);
            }
          });
        }
      });
    }

    summary.totalAssigned = assignedMemberIds.size;

    // Process each member
    roster.forEach(member => {
      const processedMember = this.processSingleMember(member);
      
      // Company distribution
      summary.byCompany[processedMember.company] = (summary.byCompany[processedMember.company] || 0) + 1;
      
      // Location distribution
      summary.byLocation[processedMember.displayLocation] = (summary.byLocation[processedMember.displayLocation] || 0) + 1;
      
      // Function distribution
      summary.byFunction[processedMember.displayFunction] = (summary.byFunction[processedMember.displayFunction] || 0) + 1;
      
      // Allocation status (simplified for summary)
      const isAssigned = assignedMemberIds.has(member.id);
      const status = isAssigned ? 'Allocated' : 'Not Allocated';
      summary.byAllocationStatus[status] = (summary.byAllocationStatus[status] || 0) + 1;
      
      // Tag distribution
      if (Array.isArray(member.tags)) {
        member.tags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : `Tag-${tag}`;
          summary.tagDistribution[tagName] = (summary.tagDistribution[tagName] || 0) + 1;
        });
      }
      
      // Validation issues
      if (!processedMember.isValid) {
        summary.validationIssues++;
      }
    });

    return summary;
  }

  /**
   * Validate member data
   */
  validateMember(member) {
    const errors = this.getMemberValidationErrors(member);
    return errors.length === 0;
  }

  /**
   * Get member validation errors
   */
  getMemberValidationErrors(member) {
    const errors = [];
    
    if (!member.id) {
      errors.push('Missing member ID');
    }
    
    if (!member.name || member.name.trim() === '') {
      errors.push('Missing member name');
    }
    
    if (!member.company) {
      errors.push('Missing company');
    }
    
    if (!member.location) {
      errors.push('Missing location');
    }
    
    if (!member.function) {
      errors.push('Missing function');
    }
    
    return errors;
  }

  /**
   * Format member for export
   */
  formatMemberForExport(member, memberOutcomes = {}, availableTags = []) {
    const processedMember = this.processMemberWithAllocations(member, memberOutcomes, availableTags);
    
    return {
      'Member ID': member.id,
      'Name': processedMember.name,
      'Company': processedMember.company,
      'Location': processedMember.displayLocation,
      'Function': processedMember.displayFunction,
      'Role': processedMember.role,
      'Focus': processedMember.focus || '',
      'Department': processedMember.department || '',
      'Tags': Array.from(processedMember.tagNames).join(', '),
      'Applications': Array.from(processedMember.memberApplications).join(', '),
      'Allocation Status': processedMember.allocationStatus,
      'Max Allocation': processedMember.maxAllocation + '%',
      'Outcomes': processedMember.outcomeNames.join(', '),
      'Validation Issues': processedMember.validationErrors.join('; ')
    };
  }

  /**
   * Format member for display in table
   */
  formatMemberForDisplay(member, memberOutcomes = {}, availableTags = []) {
    return this.processMemberWithAllocations(member, memberOutcomes, availableTags);
  }

  /**
   * Batch process members with allocation data
   */
  batchProcessMembersWithAllocations(roster, allocationResults, availableTags = []) {
    return roster.map(member => {
      const memberOutcomes = allocationResults.get(member.id) || {};
      return this.processMemberWithAllocations(member, memberOutcomes, availableTags);
    });
  }

  /**
   * Clear all caches
   */
  clearCache() {
    console.log('🧹 MemberDataProcessor: Clearing all caches');
    this.processedMemberCache.clear();
    this.tagConversionCache.clear();
    this.lastTagsHash = '';
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      processedMemberCacheSize: this.processedMemberCache.size,
      tagConversionCacheSize: this.tagConversionCache.size,
      lastTagsHashLength: this.lastTagsHash.length
    };
  }

  /**
   * Create member comparison for sorting
   */
  createMemberComparator(sortField = 'name', sortDirection = 'asc') {
    return (a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle special cases
      if (sortField === 'maxAllocation') {
        aValue = a.maxAllocation || 0;
        bValue = b.maxAllocation || 0;
      } else if (sortField === 'outcomeCount') {
        aValue = a.outcomeNames?.length || 0;
        bValue = b.outcomeNames?.length || 0;
      } else {
        // String comparison
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (sortDirection === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    };
  }

  /**
   * Filter members by criteria
   */
  filterMembers(members, filterCriteria) {
    return members.filter(member => {
      // Search filter
      if (filterCriteria.search) {
        const searchTerm = filterCriteria.search.toLowerCase();
        if (!member.name.toLowerCase().includes(searchTerm)) {
          return false;
        }
      }
      
      // Company filter
      if (filterCriteria.companies && filterCriteria.companies.length > 0) {
        if (!filterCriteria.companies.includes(member.company)) {
          return false;
        }
      }
      
      // Location filter
      if (filterCriteria.locations && filterCriteria.locations.length > 0) {
        if (!filterCriteria.locations.includes(member.location)) {
          return false;
        }
      }
      
      // Function filter
      if (filterCriteria.functions && filterCriteria.functions.length > 0) {
        if (!filterCriteria.functions.includes(member.function)) {
          return false;
        }
      }
      
      // Allocation status filter
      if (filterCriteria.allocationStatuses && filterCriteria.allocationStatuses.length > 0) {
        if (!filterCriteria.allocationStatuses.includes(member.allocationStatus)) {
          return false;
        }
      }
      
      return true;
    });
  }
}

export { MemberDataProcessor };